import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import api from '@/lib/api';
import { AdminLoadError } from '../components/AdminLoadError';

const NONE = '__none__';

interface TodoListOption {
  listId: string;
  name: string;
}

interface Workspace {
  holderId: string;
  holderType: 'personal' | 'org';
  name: string;
}

export function AdminRoadmapPage() {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [holderId, setHolderId] = useState<string>('');
  const [lists, setLists] = useState<TodoListOption[]>([]);
  const [bugsListId, setBugsListId] = useState<string>(NONE);
  const [featuresListId, setFeaturesListId] = useState<string>(NONE);
  const [loading, setLoading] = useState(true);
  const [listsLoading, setListsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sweeping, setSweeping] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const result = await api.getRoadmapSettingsAdmin();
      setWorkspaces(result.workspaces);
      setHolderId(result.settings.holderId ?? result.workspaces[0]?.holderId ?? '');
      setLists(result.lists);
      setBugsListId(result.settings.bugsListId ?? NONE);
      setFeaturesListId(result.settings.featuresListId ?? NONE);
    } catch (err) {
      console.error('Failed to load roadmap settings:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Switching workspace loads that workspace's lists. The previously-chosen
  // lists belong to the old workspace, so reset them — the admin re-picks from
  // the new set.
  const handleWorkspaceChange = useCallback(async (newHolderId: string) => {
    setHolderId(newHolderId);
    setBugsListId(NONE);
    setFeaturesListId(NONE);
    setListsLoading(true);
    try {
      const r = await api.getRoadmapListsAdmin(newHolderId);
      setLists(r.lists);
    } catch (err) {
      console.error('Failed to load workspace lists:', err);
      setLists([]);
    } finally {
      setListsLoading(false);
    }
  }, []);

  const handleSave = async () => {
    if (!holderId) return;
    setSaving(true);
    try {
      await api.setRoadmapSettingsAdmin({
        holderId,
        bugsListId: bugsListId === NONE ? null : bugsListId,
        featuresListId: featuresListId === NONE ? null : featuresListId,
      });
      toast({ title: 'Roadmap lists saved' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleSweep = async () => {
    setSweeping(true);
    try {
      const result = await api.runRoadmapSweepAdmin();
      toast({
        title: 'Sweep complete',
        description: `${result.processed} processed, ${result.paused} paused, ${result.failed} failed.`,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sweep failed';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setSweeping(false);
    }
  };

  const listSelect = (value: string, onChange: (v: string) => void) => (
    <Select value={value} onValueChange={onChange} disabled={listsLoading || lists.length === 0}>
      <SelectTrigger className="w-full sm:w-[320px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE}>None (pause auto-routing)</SelectItem>
        {lists.map((l) => (
          <SelectItem key={l.listId} value={l.listId}>
            {l.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Roadmap automation</h2>
        <p className="text-sm text-muted-foreground">
          New bug reports and suggestions are matched against your designated todo lists — a close
          match gets a +1, otherwise a new item is created. Pick a workspace and which of its todo
          lists receive each kind. Leave a list as “None” to pause auto-routing for that kind.
        </p>
      </div>

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-10 w-80 rounded" />
          <Skeleton className="h-10 w-80 rounded" />
        </div>
      ) : error ? (
        <AdminLoadError what="roadmap settings" onRetry={fetchSettings} />
      ) : (
        <>
          <div className="space-y-2">
            <Label>Workspace</Label>
            <Select value={holderId} onValueChange={handleWorkspaceChange}>
              <SelectTrigger className="w-full sm:w-[320px]">
                <SelectValue placeholder="Select a workspace" />
              </SelectTrigger>
              <SelectContent>
                {workspaces.map((w) => (
                  <SelectItem key={w.holderId} value={w.holderId}>
                    {w.name}
                    {w.holderType === 'org' ? ' (workspace)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {!listsLoading && lists.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              This workspace has no todo lists yet. Create one in the Todo Lists arche, or pick a
              different workspace above.
            </p>
          ) : (
            <>
              <div className="space-y-2">
                <Label>Bug reports go to</Label>
                {listSelect(bugsListId, setBugsListId)}
              </div>
              <div className="space-y-2">
                <Label>Suggestions (feature requests) go to</Label>
                {listSelect(featuresListId, setFeaturesListId)}
              </div>
            </>
          )}

          <div className="flex items-center gap-3">
            <Button onClick={handleSave} disabled={saving || !holderId}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button variant="outline" onClick={handleSweep} disabled={sweeping}>
              {sweeping ? 'Processing…' : 'Process pending now'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
